/* eslint-disable no-nested-ternary */
import React, { useMemo } from "react";

import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";

import { PASSWORD_MAX_STRENGTH } from "../../../../../constants";
import {
  StrengthBarContainer,
  PasswordStrengthText,
  ProgressBarContainer,
  ProgressSegment,
} from "./styles";

interface PasswordStrengthBarProps {
  password: string;
}

const CUSTOM_COLORS = {
  warning: colors.warning600,
  success: colors.success600,
  gray: colors.gray300,
};

const calculateStrength = (password: string): number => {
  let fulfilledConditions = 0;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasSpecialChar = /[#@$&]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const isLongEnough = password.length >= PASSWORD_MAX_STRENGTH;

  if (hasUppercase && hasLowercase) fulfilledConditions += 1;
  if (hasSpecialChar) fulfilledConditions += 1;
  if (hasNumber) fulfilledConditions += 1;
  if (isLongEnough) fulfilledConditions += 1;

  return fulfilledConditions;
};

const PasswordStrengthBar: React.FC<PasswordStrengthBarProps> = ({ password }) => {
  const { t: translate } = useTranslation();
  const fulfilledConditions = useMemo(() => calculateStrength(password), [password]);

  const segments = [
    {
      color:
        fulfilledConditions >= 3
          ? CUSTOM_COLORS.success
          : fulfilledConditions >= 0 && password?.length > 0
          ? CUSTOM_COLORS.warning
          : CUSTOM_COLORS.gray,
    },
    {
      color:
        fulfilledConditions >= 3
          ? CUSTOM_COLORS.success
          : fulfilledConditions >= 2
          ? CUSTOM_COLORS.warning
          : CUSTOM_COLORS.gray,
    },
    { color: fulfilledConditions >= 3 ? CUSTOM_COLORS.success : CUSTOM_COLORS.gray },
    { color: fulfilledConditions >= 4 ? CUSTOM_COLORS.success : CUSTOM_COLORS.gray },
  ];

  const totalSegments = segments.length;
  const gap = 1;
  const segmentWidth = (100 - gap * (totalSegments - 1)) / totalSegments;

  const progressValue = Math.min(fulfilledConditions * 25, 100);

  const strengthLabel =
    fulfilledConditions <= 1 && password?.length > 0
      ? translate("weakPassword")
      : fulfilledConditions === 2
      ? translate("avaragePassword")
      : fulfilledConditions === 3
      ? translate("goodPassword")
      : fulfilledConditions === 4
      ? translate("strongPassword")
      : "";

  return (
    <StrengthBarContainer>
      <PasswordStrengthText
        variant="caption"
        color={
          segments[fulfilledConditions >= 1 ? fulfilledConditions - 1 : 0]?.color ||
          CUSTOM_COLORS.gray
        }
      >
        {strengthLabel}
      </PasswordStrengthText>
      <ProgressBarContainer style={{ backgroundColor: colors.white }}>
        {segments.map((segment, index) => {
          const segmentStart = (segmentWidth + gap) * index;
          const currentLimit = Math.min(progressValue, 100);

          return (
            <ProgressSegment
              key={index}
              width={`${segmentWidth}%`}
              color={segment.color}
              left={`${segmentStart}%`}
              borderRadius={
                index === 0 ? "4px 0 0 4px" : index === totalSegments - 1 ? "0 4px 4px 0" : 0
              }
            />
          );
        })}
      </ProgressBarContainer>
    </StrengthBarContainer>
  );
};

export default PasswordStrengthBar;
